import { RefObject, useCallback, useEffect } from 'react';

interface UseAutoResizeTextareaOptions {
  ref: RefObject<HTMLTextAreaElement | null>;
  value: string;
  maxRows?: number;
}

/**
 * Resizes a textarea between 1 row and `maxRows` based on its content.
 * Adds an internal scrollbar past `maxRows`.
 *
 * When the textarea is empty, height is pinned to one line + padding —
 * some browsers (notably iOS Safari) include the wrapped placeholder's
 * height in scrollHeight, which would otherwise grow the textarea.
 */
export const useAutoResizeTextarea = ({ ref, value, maxRows = 3 }: UseAutoResizeTextareaOptions) => {
  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    const lineHeight = parseFloat(window.getComputedStyle(el).lineHeight) || 24;
    const paddingY = parseFloat(window.getComputedStyle(el).paddingTop)
      + parseFloat(window.getComputedStyle(el).paddingBottom);
    const minHeight = lineHeight + paddingY;
    const maxHeight = lineHeight * maxRows + paddingY;
    const measured = el.value ? el.scrollHeight : minHeight;
    el.style.height = `${Math.min(measured, maxHeight)}px`;
    el.style.overflowY = measured > maxHeight ? 'auto' : 'hidden';
  }, [ref, maxRows]);

  useEffect(() => {
    resize();
  }, [value, resize]);

  return { resize };
};
